import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../context/AuthContext";
import { loginRequest, SSO_CONFIGURED, ALLOWED_DOMAIN } from "../lib/msal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, ssoLogin } = useAuth();
  const { instance, accounts, inProgress } = useMsal();
  const nav = useNavigate();
  const loc = useLocation();
  // Iter67: e-mail deep-linkovi (npr. "Predaj zahtjev") vraćaju korisnika na ciljanu stranicu nakon prijave
  const dest = loc.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  const [banner, setBanner] = useState("");
  // Iter78: konfigurabilan opis (default fallback ako admin ne postavi vlastiti)
  const DEFAULT_LOGIN_DESC = "Osobni portal Kineziološkog fakulteta — digitalno upravljanje godišnjim odmorom, radom na daljinu, evidencijom prisutnosti i rješenjima s potpisom dekana. Sve u jednom mjestu.";
  const [loginDesc, setLoginDesc] = useState(DEFAULT_LOGIN_DESC);
  // Iter75e: vidljiv error box umjesto samo toast-a (toast nestane; user mora imati trajnu vidljivu poruku)
  const [ssoError, setSsoError] = useState("");
  // Iter73: kad admin isključi lokalnu prijavu — sakrij formu i ponudi je samo eksplicitno (admin fail-safe)
  const [localLoginEnabled, setLocalLoginEnabled] = useState(true);
  const [showAdminFallback, setShowAdminFallback] = useState(false);

  // Iter45: dohvati banner za kolektivni godišnji odmor (javni endpoint).
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vacation/info`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        setBanner(d?.login_banner_text || "");
        // Iter73: default true (backwards compat); false samo ako admin eksplicitno isključi
        if (d && d.local_login_enabled === false) setLocalLoginEnabled(false);
        // Iter78: custom opis (ako je postavljen)
        if (d?.login_description_text) setLoginDesc(d.login_description_text);
      })
      .catch(() => {});
  }, []);

  // Iter75e: watch MSAL accounts state — pouzdaniji signal od handleRedirectPromise
  // (koji može biti "consumed" prije nego useEffect stigne). Kad MSAL ima aktivni account
  // A JOŠ NISMO obradili backend prijavu → obradi ju.
  const [processingBackendLogin, setProcessingBackendLogin] = useState(false);
  useEffect(() => {
    if (!SSO_CONFIGURED) return;
    if (processingBackendLogin) return;
    if (inProgress !== "none") {
      console.debug("[Login] MSAL inProgress:", inProgress);
      return;
    }
    if (accounts.length === 0) return;
    const account = accounts[0];
    console.info("[Login] MSAL account detected, starting backend SSO login for:", account.username);
    setProcessingBackendLogin(true);
    setSsoBusy(true);
    setSsoError("");
    (async () => {
      try {
        // Iter75f: NE odbijaj client-side na `account.username` (UPN može biti u drugoj domeni od primarnog maila,
        // npr. UPN=@kif.hr ali stvarni mail=@kif.unizg.hr). Backend/msal-login provjerava domenu na Graph.mail polju.
        console.debug("[Login] Acquiring access token silently...");
        const tokenResp = await instance.acquireTokenSilent({ ...loginRequest, account });
        console.debug("[Login] Got access token, calling backend /api/auth/msal-login...");
        await ssoLogin(tokenResp.accessToken, account.username, account.name);
        console.info("[Login] Backend SSO login successful, navigating to:", dest);
        toast.success("Dobrodošli!");
        nav(dest, { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = detail || err.message || "SSO prijava nije uspjela (nepoznata greška)";
        console.error("[Login] SSO backend login FAILED:", msg, err);
        setSsoError(msg);
        toast.error(msg);
        // Ako je backend odbio (npr. user nije u bazi) — logout iz MSAL-a da se ne petljamo u loop
        try { await instance.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin + "/login" }); }
        catch (le) { console.debug("[Login] logout after failure error:", le?.message); }
      } finally {
        setSsoBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, inProgress]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Dobrodošli!");
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri prijavi");
    } finally {
      setBusy(false);
    }
  };

  const onSso = async () => {
    if (!SSO_CONFIGURED) {
      toast.error("SSO nije konfiguriran. Kontaktirajte IT.");
      return;
    }
    setSsoBusy(true);
    try {
      // Iter75d: redirect flow (not popup) — cijela stranica ide na Microsoft pa natrag.
      // Rezultat se obrađuje u useEffect s handleRedirectPromise() iznad kad se stranica ponovo učita.
      await instance.loginRedirect(loginRequest);
      // Kontrolu ne dohvaćamo ovdje — browser napušta stranicu.
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "SSO prijava nije uspjela");
      setSsoBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:"radial-gradient(circle at 20% 30%, hsl(var(--accent)) 0, transparent 40%), radial-gradient(circle at 80% 70%, hsl(var(--accent)) 0, transparent 40%)"}}/>
        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <img
              src="/assets/kif-logo-wide.jpg"
              alt="Sveučilište u Zagrebu — Kineziološki fakultet"
              className="h-16 w-auto object-contain"
              style={{ filter: "invert(1) brightness(1.15) contrast(1.05)", mixBlendMode: "screen" }}
              data-testid="login-kif-logo"
            />
          </div>
          <h1 className="serif text-5xl xl:text-6xl leading-[1.05] font-semibold mb-6">
            <span className="text-accent">KIF</span><br/>portal zaposlenika<span className="text-accent">.</span>
          </h1>
          <p className="opacity-80 max-w-md leading-relaxed whitespace-pre-line" data-testid="login-description">
            {loginDesc}
          </p>
        </div>
        {/* Iter45b: konfigurabilan banner — koristi text iz Postavki (umjesto hard-coded) */}
        {banner && (
          <div className="relative text-xs opacity-60 max-w-md" data-testid="login-banner">
            {(() => {
              const m = banner.match(/^(.*?):\s*(.+)$/);
              if (m) {
                return <>{m[1]}: <span className="font-medium text-accent">{m[2]}</span></>;
              }
              return banner;
            })()}
          </div>
        )}
      </div>
      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-border/60 shadow-lg">
          <CardHeader>
            <CardTitle className="serif text-3xl">Prijava</CardTitle>
            <p className="text-sm text-muted-foreground">Koristite svoj službeni ime.prezime@kif.hr račun</p>
          </CardHeader>
          <CardContent>
            <Button type="button" onClick={onSso} disabled={ssoBusy || !SSO_CONFIGURED} variant="outline" className="w-full mb-4 border-primary/30" data-testid="sso-login-btn">
              {ssoBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (
                <svg className="w-4 h-4 mr-2" viewBox="0 0 23 23" fill="none"><rect width="10" height="10" fill="#F25022"/><rect x="12" width="10" height="10" fill="#7FBA00"/><rect y="12" width="10" height="10" fill="#00A4EF"/><rect x="12" y="12" width="10" height="10" fill="#FFB900"/></svg>
              )}
              Prijava preko Microsoft 365
            </Button>
            {!SSO_CONFIGURED && (
              <p className="text-[11px] text-muted-foreground text-center -mt-2 mb-3">SSO čeka konfiguraciju (AZURE_TENANT_ID / CLIENT_ID)</p>
            )}
            {/* Iter75e: trajni error box da user vidi razlog neuspjeha (toast nestane) */}
            {ssoError && (
              <div
                className="mb-3 p-3 rounded-md border border-red-300 bg-red-50 text-red-900 text-xs space-y-1"
                data-testid="sso-error-box"
              >
                <div className="font-semibold">SSO prijava nije uspjela</div>
                <div className="opacity-90">{ssoError}</div>
                <button
                  type="button"
                  onClick={() => setSsoError("")}
                  className="underline text-[11px] opacity-70 hover:opacity-100"
                >
                  Zatvori
                </button>
              </div>
            )}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border"/></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-muted-foreground">ili e-mail i lozinka</span></div>
            </div>
            {/* Iter73: kad admin isključi lokalnu prijavu — sakrij formu, admin fail-safe iza toggle-a */}
            {!localLoginEnabled && !showAdminFallback && (
              <div className="text-center space-y-2 py-2" data-testid="local-login-disabled-notice">
                <p className="text-xs text-muted-foreground">
                  Lokalna prijava e-mailom i lozinkom je onemogućena. Molimo koristite <b>Microsoft 365</b> prijavu iznad.
                </p>
                <button
                  type="button"
                  onClick={() => setShowAdminFallback(true)}
                  className="text-[11px] text-muted-foreground/70 underline hover:text-muted-foreground"
                  data-testid="show-admin-fallback-btn"
                >
                  Admin lokalna prijava (fail-safe)
                </button>
              </div>
            )}
            {(localLoginEnabled || showAdminFallback) && (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="local-login-form">
              {!localLoginEnabled && showAdminFallback && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Fail-safe pristup: samo admin račun može ući ovim putem. Ostali zaposlenici moraju koristiti Microsoft 365.
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required placeholder="ime.prezime@kif.unizg.hr" data-testid="login-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Lozinka</Label>
                <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="login-password" />
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Prijava
              </Button>
              {localLoginEnabled && (
                <div className="text-xs text-muted-foreground text-center pt-2">
                  Početna lozinka za sve zaposlenike: <code className="bg-secondary px-1.5 py-0.5 rounded">kif2026</code>
                </div>
              )}
              <div className="text-[11px] text-muted-foreground text-center border-t pt-3 mt-3">
                {SSO_CONFIGURED ? `SSO konfiguriran za @${ALLOWED_DOMAIN}` : `Buduća integracija: Microsoft 365 (SSO) — pripremljena u sustavu`}
              </div>
            </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
